import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { searchAuditLogs, fetchLatestRunByTraceId } from '../services/auditLogsService';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  SectionTitle,
  SearchInput,
  Select,
  Label,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  StatusBadge,
  Modal,
  ModalContent,
  MarkdownContainer,
} from '../styles/PageStyles';
import ReactMarkdown from 'react-markdown';

const Layout = styled.div`
  display: grid;
  grid-template-columns: ${props => props.focusMode ? '0px 1fr 0px' : props.detailsFullscreen ? '0px 1fr' : `${props.showFilters ? '280px' : '0px'} 1fr ${props.showDetails ? '420px' : '0px'}`};
  gap: 1rem;
  transition: grid-template-columns 0.25s ease;
`;

const FiltersPanel = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  padding: 0.75rem;
  overflow: hidden auto;
`;

const FiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const HideBtn = styled.button`
  border: none;
  background: transparent;
  font-size: 0.85rem;
  color: var(--text-secondary);
  cursor: pointer;
`;

const FilterGroup = styled.div`
  border-top: 1px solid #e5e7eb;
  padding-top: 0.75rem;
  margin-top: 0.75rem;
`;

const GroupTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: #334155;
  margin-bottom: 0.5rem;
`;

const CountBadge = styled.span`
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  font-size: 0.75rem;
  margin-left: 0.5rem;
  visibility: 0;
  display:none;
`;

const QuickItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 0.4rem;
  cursor: pointer;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
  cursor: pointer;
`;

const LeftSearch = styled.input`
  width: 100%;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-bottom: 0.5rem;
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto auto auto;
  gap: 0.5rem;
  align-items: center;
`;

const Examples = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
  white-space: pre-line;
  background: #fafafa;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
`;

const ChipsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.25rem 0.6rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  font-size: 0.8rem;
  color: #334155;
  cursor: default;
`;

const ChipClose = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  font-size: 0.9rem;
`;

const TimelineCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  padding: 0.75rem 1rem;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const Bars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 80px;
`;

const Bar = styled.div`
  width: 3px;
  background: linear-gradient(180deg, rgba(0,102,255,0.6), rgba(0,102,255,0.2));
  border-radius: 2px 2px 0 0;
`;

const TableHeaderRow = styled.tr`
  background: #fafafa;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ConsoleToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const ConsoleList = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #0b1220;
  color: #e2e8f0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 12.5px;
  height: calc(100vh - 280px);
  overflow: auto;
`;

const ConsoleLine = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$showPrefix ? 'auto 1fr auto' : '1fr auto'};
  gap: 0.75rem;
  padding: 6px 10px;
  border-bottom: 1px solid rgba(148,163,184,0.12);
  align-items: flex-start;
  cursor: pointer;
  &:hover { background: rgba(148,163,184,0.06); }
`;

const MetaPrefix = styled.span`
  color: #93a4b7;
  white-space: nowrap;
`;

const MessageText = styled.span`
  color: #e2e8f0;
  white-space: ${props => props.$wrap ? 'pre-wrap' : 'pre'};
  word-break: break-word;
`;

const CopyBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(148,163,184,0.25);
  color: #93a4b7;
  border-radius: 6px;
  font-size: 11px;
  padding: 2px 6px;
  cursor: pointer;
  &:hover { background: rgba(148,163,184,0.12); }
`;

const DetailsPanel = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  ${props => props.$fullscreen && `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    border-radius: 0;
    margin: 0;
  `}
`;

const DetailsHeader = styled.div`
  padding: 0.8rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DrawerTitle = styled.div`
  font-weight: 700;
  font-size: 1.05rem;
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 6px;
  color: #64748b;
  transition: all 0.2s;
  
  &:hover {
    background: #f1f5f9;
    color: #334155;
  }
`;

const DrawerContent = styled.div`
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 0.4rem 0.75rem;
`;

const K = styled.div`
  font-weight: 600;
  color: #334155;
  font-size: 0.9rem;
`;

const V = styled.div`
  color: #334155;
  font-size: 0.9rem;
  word-break: break-word;
`;

const MonoBlock = styled.pre`
  background: #0f172a;
  color: #e2e8f0;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  overflow: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-word;
`;

const RelatedList = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const RelatedItem = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 0.5rem 1rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const ControlsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(140px, 1fr));
  gap: 0.75rem;
  align-items: end;
`;

const SmallText = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

// Run details modal layout
const RunGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
  align-items: stretch;
  /* Fill modal minus some space for footer */
  height: calc(95dvh - 160px);
`;

const RunCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0; /* allow children to size using flex */
`;

const RunHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 700;
  color: #1e3a8a;
`;

const LightMono = styled.pre`
  background: #f8fafc;
  color: #0f172a;
  border: 1px solid #e5e7eb;
  margin: 0.75rem;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  /* Fill available height inside RunCard beneath header */
  flex: 1 1 auto;
  min-height: 220px;
  max-height: none;
  overflow: auto;
  font-size: 12.5px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ScrollBody = styled.div`
  background: #f8fafc;
  color: #0f172a;
  border: 1px solid #e5e7eb;
  margin: 0.75rem;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  flex: 1 1 auto;
  min-height: 220px;
  max-height: none;
  overflow: auto;
  font-size: 13px;
`;

const TIME_RANGE_OPTIONS = [
  { label: 'Last 30 min', hours: 0.5 },
  { label: 'Last 1 hour', hours: 1 },
  { label: 'Last 2 hours', hours: 2 },
  { label: 'Last 6 hours', hours: 6 },
  { label: 'Last 12 hours', hours: 12 },
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Custom range', hours: null },
];
const defaultTimeHours = 12;
const defaultPageSize = 25;

const services = ['pipeline-executor','agent-executor','planner','classifier'];
const statuses = ['success','failed','running'];
const eventTypes = ['pipeline_log','agent_log','planner_log','classifier_log'];
const users = ['blue@metamorphtech.com','dev@metamorphtech.com'];
const traceIds = ['claim-session-001','claim-session-002','claim-session-003','claim-session-004'];

// New filter option domains
const moduleOptions = [
  'base_agent',
  'base_planner',
  'base_step_executor',
  'pipeline_runner',
];

const functionOptions = [
  '_a_execute_pipeline_nodes',
  '_evaluate_next_node',
  '_generate_pipeline_summary',
  '_parse_and_build_raw_content',
  'aexecute',
  'astart_pipeline',
  'on_arun',
  'on_arun_with_streaming',
  'on_run',
  'on_run_with_streaming',
];

const loggerNameOptions = [
  'mms_ai_agents.agents.dynamic_agent',
  'mms_ai_planner.planners.dynamic_planner',
  'mms_ai_workflows.node_executors.agent_node_executor',
  'mms_ai_workflows.node_executors.code_node_executor',
  'mms_ai_workflows.node_executors.entity_operation_node_executor',
  'mms_ai_workflows.runner.pipeline_runner',
];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const generateMessage = (svc) => {
  const msgs = {
    'pipeline-executor': [
      'Pipeline execution started',
      'Pipeline completed successfully',
      'Validating inputs',
      'Aggregating results',
    ],
    'agent-executor': [
      'Agent produced a response',
      'Async running agent: Default System Agent with input...',
      'Summary agent finished',
    ],
    'planner': [
      'Generated plan with 4 steps',
      'Selected best tool',
      'Refined user goal',
    ],
    'classifier': [
      'Classified intent as CLAIM_APPROVAL',
      'Extracted entities',
    ],
  };
  return pick(msgs[svc] || ['Log event']);
};

const generateStack = () => {
  return `Traceback (most recent call last):\n  File \'/app/executor.py\', line 42, in run\n    result = task()\n  File \'/app/agent.py\', line 88, in produce\n    raise RuntimeError(\'Agent failed\')\nRuntimeError: Agent failed`;
};

const generateMockLogs = (count = 1200) => {
  const now = Date.now();
  const start = now - 24 * 60 * 60 * 1000;
  const arr = [];
  for (let i = 0; i < count; i++) {
    const ts = new Date(start + Math.random() * (now - start));
    const service = pick(services);
    const level = pick(['INFO','INFO','WARNING','ERROR','DEBUG']);
    const status = level === 'ERROR' ? 'failed' : pick(statuses);
    const evt = pick(eventTypes);
    const id = `${ts.getTime()}-${i}`;
    const tId = pick(traceIds);
    arr.push({
      id,
      timestamp: ts.toISOString(),
      level,
      service_name: service,
      event_type: evt,
      status,
      user: pick(users),
      request_id: cryptoRandomId(),
      trace_id: tId,
      span_id: cryptoRandomId(),
      message: generateMessage(service),
      module: pick(moduleOptions),
      function_name: pick(functionOptions),
      logger_name: pick(loggerNameOptions),
      business_identifiers: service.includes('pipeline') ? {
        claim_id: tId.replace('claim-session-', 'CLM-2026-00'),
        patient_name: pick(['John Smith','Jane Doe','Bob Lee','Aisha Khan']),
        amount_claimed: pick([12000, 24000, 45000, 8000]),
      } : undefined,
      stack_trace: level === 'ERROR' ? generateStack() : undefined,
      metadata: { service_version: 'v1.2.3' },
    });
  }
  return arr.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

function cryptoRandomId() {
  const bytes = new Uint8Array(8);
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 8; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

const withinHours = (iso, hours) => {
  const t = new Date(iso).getTime();
  const now = Date.now();
  if (hours == null) return true;
  return t >= now - hours * 60 * 60 * 1000;
};

const freeTextMatch = (rec, q) => {
  if (!q) return true;
  const s = q.toLowerCase();
  const hay = [
    rec.message,
    rec.service_name,
    rec.level,
    rec.status,
    rec.user,
    rec.request_id,
    rec.trace_id,
    rec.span_id,
    rec.event_type,
    rec.module,
    rec.function_name,
    rec.logger_name,
    ...(rec.business_identifiers ? Object.values(rec.business_identifiers) : []),
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());
  return hay.some((v) => v.includes(s));
};

const buildTimeline = (logs, hours) => {
  const now = Date.now();
  const hrs = hours == null ? 24 : hours;
  const start = now - hrs * 60 * 60 * 1000;
  const minutes = Math.max(1, Math.floor((now - start) / (60 * 1000)));
  const targetBars = 120;
  const bucketSize = Math.max(1, Math.floor(minutes / targetBars));
  const buckets = [];
  for (let i = 0; i < minutes; i += bucketSize) {
    const bucketStart = start + i * 60 * 1000;
    const bucketEnd = Math.min(start + (i + bucketSize) * 60 * 1000, now);
    const count = logs.filter((r) => {
      const t = new Date(r.timestamp).getTime();
      return t >= bucketStart && t < bucketEnd;
    }).length;
    buckets.push({ t: bucketStart, count });
  }
  const max = buckets.reduce((m, b) => Math.max(m, b.count), 0) || 1;
  return { buckets, max };
};

const aggregateCounts = (rows) => {
  const byLevel = {};
  const byModule = {};
  const byFunction = {};
  const byLogger = {};
  rows.forEach((r) => {
    byLevel[r.level] = (byLevel[r.level] || 0) + 1;
    if (r.module) byModule[r.module] = (byModule[r.module] || 0) + 1;
    if (r.function_name) byFunction[r.function_name] = (byFunction[r.function_name] || 0) + 1;
    if (r.logger_name) byLogger[r.logger_name] = (byLogger[r.logger_name] || 0) + 1;
  });
  return { byLevel, byModule, byFunction, byLogger };
};

export default function AuditLogs() {
  const [allLogs, setAllLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [keyword, setKeyword] = useState('');
  const [timeRangeHours, setTimeRangeHours] = useState(defaultTimeHours);
  const [levelFilter, setLevelFilter] = useState('INFO');
  const [serviceFilters, setServiceFilters] = useState([]); // legacy, not shown in UI
  const [eventTypeFilters, setEventTypeFilters] = useState([]); // legacy, not shown in UI
  const [statusFilters, setStatusFilters] = useState([]); // legacy, not shown in UI
  const [userFilter, setUserFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('overview');
  const [showFilters, setShowFilters] = useState(true);
  const [serviceQuery, setServiceQuery] = useState(''); // legacy
  const [moduleQuery, setModuleQuery] = useState('');
  const [moduleFilters, setModuleFilters] = useState([]);
  const [functionFilters, setFunctionFilters] = useState([]);
  const [loggerFilters, setLoggerFilters] = useState([]);
  const [devView, setDevView] = useState(false);
  const [wrapLines, setWrapLines] = useState(true);
  const [focusMode, setFocusMode] = useState(false);
  const [showPrefix, setShowPrefix] = useState(true);
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);
  const [detailsFullscreen, setDetailsFullscreen] = useState(false);
  const [serverTotal, setServerTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serverHasNext, setServerHasNext] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  // Run details popup state
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [runError, setRunError] = useState(null);
  const [runDetails, setRunDetails] = useState(null);

  const openRunModalForTrace = async (row) => {
    try {
      if (!row?.trace_id) return;
      setRunModalOpen(true);
      setRunLoading(true);
      setRunError(null);
      const data = await fetchLatestRunByTraceId(row.trace_id);
      setRunDetails(data || null);
    } catch (e) {
      setRunDetails(null);
      setRunError(e?.message || 'Failed to load run details');
    } finally {
      setRunLoading(false);
    }
  };

  // Fetch logs from backend when filters/time/page/pageSize change
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const payload = {
          keyword: keyword || null,
          min_level: levelFilter || 'INFO',
          modules: moduleFilters,
          functions: functionFilters,
          logger_names: loggerFilters,
          since_hours: timeRangeHours == null ? 24 : timeRangeHours,
          from_date: null,
          to_date: null,
          page,
          page_size: pageSize,
        };
        const resp = await searchAuditLogs(payload);
        let items = [];
        let total = null;
        let hasNextFromServer = null;
        if (Array.isArray(resp)) {
          items = resp;
        } else if (resp && typeof resp === 'object') {
          const raw = resp.logs || resp.items || resp.results || resp.data?.items || resp.data?.results || [];
          // Map API fields to UI fields
          items = (raw || []).map((x) => ({
            ...x,
            function_name: x.function_name ?? x.function,
            user: x.user ?? x.user_id ?? x.created_by ?? null,
          }));
          total = resp.total || resp.total_count || resp.count || resp.data?.total || null;
          if (typeof resp.has_next === 'boolean') hasNextFromServer = resp.has_next;
        }
        setAllLogs(items || []);
        setServerTotal(typeof total === 'number' ? total : null);
        setServerHasNext(hasNextFromServer);
      } catch (e) {
        console.error('Failed to fetch audit logs', e);
        setError(e?.message || 'Failed to fetch logs');
        setAllLogs([]);
        setServerTotal(null);
        setServerHasNext(null);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [keyword, levelFilter, moduleFilters, functionFilters, loggerFilters, timeRangeHours, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [keyword, timeRangeHours, levelFilter, serviceFilters, eventTypeFilters, statusFilters, userFilter, pageSize, moduleFilters, functionFilters, loggerFilters]);

  const timeFiltered = useMemo(() => allLogs.filter((r) => withinHours(r.timestamp, timeRangeHours)), [allLogs, timeRangeHours]);
  const { byLevel, byModule, byFunction, byLogger } = useMemo(() => aggregateCounts(timeFiltered), [timeFiltered]);

  const filtered = useMemo(() => {
    return timeFiltered.filter((r) =>
      (serviceFilters.length === 0 || serviceFilters.includes(r.service_name)) &&
      (eventTypeFilters.length === 0 || eventTypeFilters.includes(r.event_type)) &&
      (statusFilters.length === 0 || statusFilters.includes(r.status)) &&
      (moduleFilters.length === 0 || moduleFilters.includes(r.module)) &&
      (functionFilters.length === 0 || functionFilters.includes(r.function_name)) &&
      (loggerFilters.length === 0 || loggerFilters.includes(r.logger_name)) &&
      (!userFilter || r.user === userFilter) &&
      freeTextMatch(r, keyword)
    );
  }, [timeFiltered, serviceFilters, eventTypeFilters, statusFilters, moduleFilters, functionFilters, loggerFilters, userFilter, keyword]);

  // Timeline removed: no bucket or level aggregations needed here

  const pageRows = filtered; // server already paginates
  const hasNext = serverHasNext != null ? serverHasNext : (serverTotal != null ? page * pageSize < serverTotal : pageRows.length === pageSize);

  const toggleInArray = (list, value, setList) => {
    if (list.includes(value)) setList(list.filter((v) => v !== value));
    else setList([...list, value]);
  };

  const clearFilters = () => {
    setLevelFilter('INFO');
    setServiceFilters([]);
    setEventTypeFilters([]);
    setStatusFilters([]);
    setUserFilter(null);
    setModuleFilters([]);
    setFunctionFilters([]);
    setLoggerFilters([]);
  };

  const relatedLogs = useMemo(() => {
    if (!selected?.trace_id) return [];
    return allLogs.filter((r) => r.trace_id === selected.trace_id).slice(0, 50);
  }, [allLogs, selected]);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Audit Logs / Workflow Investigations</PageTitle>
      </PageHeader>

      <Layout showFilters={showFilters && !focusMode} showDetails={!focusMode && !!selected && !detailsCollapsed} focusMode={focusMode} detailsFullscreen={detailsFullscreen}>
        <FiltersPanel style={{ display: showFilters && !focusMode ? 'block' : 'none' }}>
          <FiltersHeader>
            <strong>Filters</strong>
            <HideBtn onClick={() => setShowFilters(false)}>Hide</HideBtn>
          </FiltersHeader>

          <div style={{ marginBottom: '1rem' }}>
            <Select value={timeRangeHours == null ? 'custom' : String(timeRangeHours)} onChange={(e) => {
              const val = e.target.value;
              setTimeRangeHours(val === 'custom' ? null : Number(val));
            }} style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              {TIME_RANGE_OPTIONS.map((opt) => (
                <option key={String(opt.hours)} value={opt.hours == null ? 'custom' : String(opt.hours)}>{opt.label}</option>
              ))}
            </Select>
          </div>

          <GroupTitle>Quick Filters</GroupTitle>
          <QuickItem onClick={() => setLevelFilter(null)} style={{ background: !levelFilter ? '#f1f5f9' : 'transparent', border: !levelFilter ? '1px solid #3b82f6' : '1px solid #e5e7eb' }}>
            <span>All Logs</span>
            <CountBadge>{timeFiltered.length.toLocaleString()}</CountBadge>
          </QuickItem>
          <QuickItem onClick={() => levelFilter === 'ERROR' ? setLevelFilter(null) : setLevelFilter('ERROR')} style={{ background: levelFilter === 'ERROR' ? '#fef2f2' : 'transparent', border: levelFilter === 'ERROR' ? '1px solid #ef4444' : '1px solid #e5e7eb' }}>
            <span>Errors</span>
            <CountBadge>{(byLevel['ERROR'] || 0).toLocaleString()}</CountBadge>
          </QuickItem>
          <QuickItem onClick={() => levelFilter === 'INFO' ? setLevelFilter(null) : setLevelFilter('INFO')} style={{ background: levelFilter === 'INFO' ? '#eff6ff' : 'transparent', border: levelFilter === 'INFO' ? '1px solid #3b82f6' : '1px solid #e5e7eb' }}>
            <span>Info</span>
            <CountBadge>{(byLevel['INFO'] || 0).toLocaleString()}</CountBadge>
          </QuickItem>
          <QuickItem onClick={() => levelFilter === 'WARNING' ? setLevelFilter(null) : setLevelFilter('WARNING')} style={{ background: levelFilter === 'WARNING' ? '#fef9c3' : 'transparent', border: levelFilter === 'WARNING' ? '1px solid #eab308' : '1px solid #e5e7eb' }}>
            <span>Warnings</span>
            <CountBadge>{(byLevel['WARNING'] || 0).toLocaleString()}</CountBadge>
          </QuickItem>

          
          {(levelFilter || moduleFilters.length || functionFilters.length || loggerFilters.length || userFilter) && (
            <FilterGroup>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <GroupTitle style={{ marginBottom: 0 }}>Active Filters</GroupTitle>
                <Button onClick={clearFilters} style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>Clear All</Button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {levelFilter && (
                  <Chip>Level: {levelFilter} <ChipClose onClick={() => setLevelFilter(null)}>✕</ChipClose></Chip>
                )}
                {moduleFilters.map((m) => (
                  <Chip key={m}>Module: {m} <ChipClose onClick={() => toggleInArray(moduleFilters, m, setModuleFilters)}>✕</ChipClose></Chip>
                ))}
                {functionFilters.map((f) => (
                  <Chip key={f}>Function: {f} <ChipClose onClick={() => toggleInArray(functionFilters, f, setFunctionFilters)}>✕</ChipClose></Chip>
                ))}
                {loggerFilters.map((l) => (
                  <Chip key={l}>Logger: {l} <ChipClose onClick={() => toggleInArray(loggerFilters, l, setLoggerFilters)}>✕</ChipClose></Chip>
                ))}
                {userFilter && (
                  <Chip>User: {userFilter} <ChipClose onClick={() => setUserFilter(null)}>✕</ChipClose></Chip>
                )}
              </div>
            </FilterGroup>
          )}

          <FilterGroup>
            <GroupTitle>Module</GroupTitle>
            <LeftSearch placeholder="Search module" value={moduleQuery} onChange={(e) => setModuleQuery(e.target.value)} />
            {Object.entries(byModule)
              .filter(([k]) => !moduleQuery || k.toLowerCase().includes(moduleQuery.toLowerCase()))
              .sort((a,b) => b[1]-a[1])
              .map(([mod, count]) => (
                <CheckboxRow key={mod}>
                  <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input type="checkbox" checked={moduleFilters.includes(mod)} onChange={() => toggleInArray(moduleFilters, mod, setModuleFilters)} />
                    <span>{mod}</span>
                  </span>
                </CheckboxRow>
              ))}
          </FilterGroup>

          <FilterGroup>
            <GroupTitle>Function</GroupTitle>
            {Object.entries(byFunction)
              .sort((a,b)=>b[1]-a[1])
              .map(([fn, count]) => (
                <CheckboxRow key={fn}>
                  <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input type="checkbox" checked={functionFilters.includes(fn)} onChange={() => toggleInArray(functionFilters, fn, setFunctionFilters)} />
                    <span>{fn}</span>
                  </span>
                </CheckboxRow>
              ))}
          </FilterGroup>

          <FilterGroup>
            <GroupTitle>Logger Name</GroupTitle>
            {Object.entries(byLogger)
              .sort((a,b)=>b[1]-a[1])
              .map(([lg, count]) => (
                <CheckboxRow key={lg}>
                  <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input type="checkbox" checked={loggerFilters.includes(lg)} onChange={() => toggleInArray(loggerFilters, lg, setLoggerFilters)} />
                    <span>{lg}</span>
                  </span>
                </CheckboxRow>
              ))}
          </FilterGroup>

        </FiltersPanel>

        <div>
          <Toolbar>
            <TopRow>
              <SearchInput
                placeholder="Search logs, claim IDs, trace IDs, users, messages..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setKeyword(search || '');
                    setPage(1);
                  }
                }}
              />
              <Button onClick={() => { setKeyword(search || ''); setPage(1); }}>Search</Button>
              <Button onClick={() => setShowFilters((s) => !s)} disabled={focusMode}>{showFilters ? 'Hide Filters' : 'Show Filters'}</Button>
              <Button onClick={() => setDevView((v) => !v)}>{devView ? 'Table View' : 'Developer View'}</Button>
              {devView && (
                <Button onClick={() => setShowPrefix((p) => !p)}>{showPrefix ? 'Hide Prefix' : 'Show Prefix'}</Button>
              )}
            </TopRow>
          </Toolbar>

          

          <SectionTitle style={{display: 'flex', justifyContent:'space-between'}}>Log Stream<div>
                  {/* <Label>Rows per page:</Label> */}
                  <Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ width: 100, display: 'inline-block', marginLeft: 8 }}>
                    {[25,50,100].map(n => <option key={n} value={n}>{n}</option>)}
                  </Select>
                </div></SectionTitle>
          {loading && <SmallText>Loading logs…</SmallText>}
          {error && <SmallText style={{ color: '#dc2626', marginBottom: '0.5rem' }}>{error}</SmallText>}
          {devView ? (
            <>
              <ConsoleList>
                {pageRows.map((row) => (
                  <ConsoleLine key={row.id} $showPrefix={showPrefix} onClick={() => { if (!focusMode) { setSelected(row); setTab('overview'); setDetailsCollapsed(false); openRunModalForTrace(row); } }}>
                    {showPrefix && <MetaPrefix>{formatPrefix(row)}</MetaPrefix>}
                    <MessageText $wrap={wrapLines}>{row.message}</MessageText>
                    <CopyBtn onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(formatFullLine(row)); }}>Copy</CopyBtn>
                  </ConsoleLine>
                ))}
              </ConsoleList>
            </>
          ) : (
            <>
              {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div>Columns</div>
                <div>
                  <Label>Rows per page:</Label>
                  <Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ width: 100, display: 'inline-block', marginLeft: 8 }}>
                    {[25,50,100].map(n => <option key={n} value={n}>{n}</option>)}
                  </Select>
                </div>
              </div> */}
              <Table>
                <thead>
                  <TableHeaderRow>
                    <TableHeader>Timestamp</TableHeader>
                    <TableHeader>Level</TableHeader>
                    <TableHeader>Service</TableHeader>
                    <TableHeader>Trace ID</TableHeader>
                    <TableHeader>Status</TableHeader>
                    {/* <TableHeader style={{ width: '50%' }}>Message</TableHeader> */}
                  </TableHeaderRow>
                </thead>
                <tbody>
                  {pageRows.map((row) => (
                    <TableRow key={row.id} onClick={() => { setSelected(row); setTab('overview'); setDetailsCollapsed(false);  }} style={{ cursor: 'pointer' }}>
                      <TableCell>{new Date(row.timestamp).toLocaleString()}</TableCell>
                      <TableCell>{row.level}</TableCell>
                      <TableCell>{row.service_name}</TableCell>
                      <TableCell style={{ color: 'var(--primary-600)' }}>{row.trace_id}</TableCell>
                      <TableCell>
                        <StatusBadge status={row.status === 'failed' ? 'inactive' : row.status === 'success' ? 'active' : 'warning'}>
                          {row.status}
                        </StatusBadge>
                      </TableCell>
                      {/* <TableCell style={{ width: '50%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {(() => {
                          const full = row.message || '';
                          const expanded = !!expandedRows[row.id];
                          const truncated = full.length > 200 ? (full.slice(0, 200) + '…') : full;
                          return (
                            <>
                              <div>{expanded ? full : truncated}</div>
                              {full.length > 200 && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setExpandedRows((m) => ({ ...m, [row.id]: !expanded })); }}
                                  style={{
                                    marginTop: 4,
                                    padding: '2px 6px',
                                    fontSize: 12,
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 6,
                                    background: '#fff',
                                    color: '#334155',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {expanded ? 'Collapse' : 'Expand'}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </TableCell> */}
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </>
          )}

          <Pagination>
            <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || loading}>◀ Prev</Button>
            <div>
              Page {page}{serverTotal != null ? ` of ${Math.max(1, Math.ceil(serverTotal / pageSize))}` : ''}
            </div>
            <Button onClick={() => setPage((p) => p + 1)} disabled={!hasNext || loading}>Next ▶</Button>
          </Pagination>
        </div>

        <DetailsPanel $fullscreen={detailsFullscreen} style={{ display: selected && !detailsCollapsed ? 'flex' : 'none' }}>
          <DetailsHeader>
            <DrawerTitle>Log Details</DrawerTitle>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <Button onClick={() => openRunModalForTrace(selected)} style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem' }}>Open Run Modal</Button>
              <CloseBtn onClick={() => setDetailsFullscreen(!detailsFullscreen)} title={detailsFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                {detailsFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </CloseBtn>
              <CloseBtn onClick={() => { setSelected(null); setDetailsCollapsed(true); setDetailsFullscreen(false); }} title="Close">
                <X size={18} />
              </CloseBtn>
            </div>
          </DetailsHeader>
          {selected && (
            <DrawerContent>
              <TabContainer>
                <TabList>
                  <Tab active={tab === 'overview'} onClick={() => setTab('overview')}>Overview</Tab>
                  <Tab active={tab === 'raw'} onClick={() => setTab('raw')}>Raw JSON</Tab>
                  <Tab active={tab === 'stack'} onClick={() => setTab('stack')}>Stack Trace</Tab>
                  <Tab active={tab === 'related'} onClick={() => setTab('related')}>Related Logs</Tab>
                </TabList>
                <TabContent active={tab === 'overview'}>
                  <Grid>
                    <K>Level</K><V>{selected.level}</V>
                    <K>Service</K><V>{selected.service_name}</V>
                    <K>Event Type</K><V>{selected.event_type || '-'}</V>
                    <K>Status</K><V>{selected.status || '-'}</V>
                    <K>Timestamp</K><V>{new Date(selected.timestamp).toUTCString()}</V>
                    <K>User</K><V>{selected.user || '-'}</V>
                    <K>Request ID</K><V>{selected.request_id || '-'}</V>
                    <K>Trace ID</K><V>{selected.trace_id || '-'}</V>
                    <K>Span ID</K><V>{selected.span_id || '-'}</V>
                    <K>Module</K><V>{selected.module || '-'}</V>
                    <K>Function</K><V>{selected.function_name || '-'}</V>
                    <K>Logger</K><V>{selected.logger_name || '-'}</V>
                  </Grid>
                  <div style={{ marginTop: '1rem' }}>
                    <Label>Message</Label>
                    <MonoBlock>{selected.message || ''}</MonoBlock>
                  </div>
                  {selected.business_identifiers && (
                    <div style={{ marginTop: '1rem' }}>
                      <Label>Extracted Business Information</Label>
                      <Grid>
                        {Object.entries(selected.business_identifiers).map(([k, v]) => (
                          <React.Fragment key={k}>
                            <K>{formatKey(k)}</K><V>{String(v)}</V>
                          </React.Fragment>
                        ))}
                      </Grid>
                    </div>
                  )}
                </TabContent>
                <TabContent active={tab === 'raw'}>
                  <MonoBlock>{JSON.stringify(selected, null, 2)}</MonoBlock>
                </TabContent>
                <TabContent active={tab === 'stack'}>
                  {selected.stack_trace ? (
                    <MonoBlock>{selected.stack_trace}</MonoBlock>
                  ) : (
                    <SmallText>No stack trace available.</SmallText>
                  )}
                </TabContent>
                <TabContent active={tab === 'related'}>
                  {relatedLogs.length === 0 ? (
                    <SmallText>No related logs for this trace.</SmallText>
                  ) : (
                    <RelatedList>
                      {relatedLogs.map((r) => (
                        <RelatedItem key={r.id}>
                          <div>{new Date(r.timestamp).toLocaleTimeString()}</div>
                          <div>
                            <strong>{r.service_name}</strong> · {r.level} · {r.status} — {r.message}
                          </div>
                        </RelatedItem>
                      ))}
                    </RelatedList>
                  )}
                </TabContent>
              </TabContainer>
            </DrawerContent>
          )}
        </DetailsPanel>

        {/* Run Details Popup */}
        {runModalOpen && (
          <Modal onClick={() => setRunModalOpen(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              {runLoading && <SmallText>Loading…</SmallText>}
              {runError && <SmallText style={{ color: '#dc2626' }}>{runError}</SmallText>}
              {!runLoading && !runError && runDetails && (
                <>
                  {(() => {
                    const metricType = (runDetails?.metric_type || '').toLowerCase();
                    const isPipeline = metricType === 'pipeline';
                    const runData = runDetails?.run_data ?? {};
                    let req;
                    let resp;
                    let wvars = runData?.session_state?.workflow_vars ?? runDetails?.session_state?.workflow_vars ?? null;

                    if (isPipeline) {
                      req = runData?.request ?? runDetails?.request ?? null;
                      resp = runData?.response ?? runDetails?.response ?? runData?.result ?? null;
                    } else if (metricType === 'agent' || metricType === 'planner') {
                      // Per spec for agent/planner: request = run_data.input.input_content, response = run_data.content
                      req = runData?.input?.input_content ?? runData?.input ?? runDetails?.request ?? null;
                      resp = runData?.content ?? runData?.response ?? runDetails?.response ?? null;
                    } else {
                      req = runData?.request ?? runDetails?.request ?? runData?.input ?? null;
                      resp = runData?.response ?? runDetails?.response ?? runData?.result ?? runData?.content ?? null;
                    }

                    if (isPipeline) {
                      return (
                        <RunGrid>
                          <RunCard>
                            <RunHeader>
                              <span>Request</span>
                              <Button onClick={() => navigator.clipboard.writeText(safeStringify(req))}>Copy</Button>
                            </RunHeader>
                            <LightMono>{safeStringify(req)}</LightMono>
                          </RunCard>
                          <RunCard>
                            <RunHeader>
                              <span>Workflow Vars</span>
                              <Button onClick={() => navigator.clipboard.writeText(safeStringify(wvars))} disabled={wvars == null}>Copy</Button>
                            </RunHeader>
                            <LightMono>{wvars == null ? 'null' : safeStringify(wvars)}</LightMono>
                          </RunCard>
                          <RunCard>
                            <RunHeader>
                              <span>Response</span>
                              <Button onClick={() => navigator.clipboard.writeText(safeStringify(resp))}>Copy</Button>
                            </RunHeader>
                            <LightMono>{safeStringify(resp)}</LightMono>
                          </RunCard>
                        </RunGrid>
                      );
                    }

                    const reqMd = typeof req === 'string' ? req : safeStringify(req);
                    const respMd = typeof resp === 'string' ? resp : safeStringify(resp);
                    return (
                      <RunGrid>
                        <RunCard>
                          <RunHeader>
                            <span>Request</span>
                            <Button onClick={() => navigator.clipboard.writeText(reqMd)}>Copy</Button>
                          </RunHeader>
                          {typeof req === 'string' ? (
                            <ScrollBody>
                              <MarkdownContainer style={{ margin: 0, maxHeight: 'none', height: '100%' }}>{<ReactMarkdown>{reqMd}</ReactMarkdown>}</MarkdownContainer>
                            </ScrollBody>
                          ) : (
                            <LightMono>{reqMd}</LightMono>
                          )}
                        </RunCard>
                        {wvars != null && (
                          <RunCard>
                            <RunHeader>
                              <span>Workflow Vars</span>
                              <Button onClick={() => navigator.clipboard.writeText(safeStringify(wvars))}>Copy</Button>
                            </RunHeader>
                            <LightMono>{safeStringify(wvars)}</LightMono>
                          </RunCard>
                        )}
                        <RunCard>
                          <RunHeader>
                            <span>Response</span>
                            <Button onClick={() => navigator.clipboard.writeText(respMd)}>Copy</Button>
                          </RunHeader>
                          {typeof resp === 'string' ? (
                            <ScrollBody>
                              <MarkdownContainer style={{ margin: 0, maxHeight: 'none', height: '100%' }}>{<ReactMarkdown>{respMd}</ReactMarkdown>}</MarkdownContainer>
                            </ScrollBody>
                          ) : (
                            <LightMono>{respMd}</LightMono>
                          )}
                        </RunCard>
                      </RunGrid>
                    );
                  })()}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                    <Button onClick={() => setRunModalOpen(false)}>Cancel</Button>
                    <Button primary onClick={() => setRunModalOpen(false)}>Ok</Button>
                  </div>
                </>
              )}
            </ModalContent>
          </Modal>
        )}
      </Layout>
    </PageContainer>
  );
}

function formatKey(input) {
  return String(input)
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function pad(n, width = 2) {
  const s = String(n);
  return s.length >= width ? s : new Array(width - s.length + 1).join('0') + s;
}

function formatTimestampMs(iso) {
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const HH = pad(d.getHours());
  const MM = pad(d.getMinutes());
  const SS = pad(d.getSeconds());
  const ms = pad(d.getMilliseconds(), 3);
  return `${yyyy}-${mm}-${dd} ${HH}:${MM}:${SS},${ms}`;
}

function formatPrefix(row) {
  const t = formatTimestampMs(row.timestamp);
  const lvl = row.level || '-';
  const svc = row.service_name || '-';
  const ev = row.event_type || '-';
  return `${t} | ${lvl} | ${svc} | ${ev}`;
}

function formatFullLine(row) {
  return `${formatPrefix(row)} | ${row.message || ''}`;
}

function safeStringify(obj) {
  try {
    if (obj == null) return 'null';
    return typeof obj === 'string' ? obj : JSON.stringify(obj, null, 2);
  } catch (e) {
    try { return String(obj); } catch { return '[unserializable]'; }
  }
}
